import React, { useState } from 'react';
import {
  HelpCircle, ChevronDown, ChevronUp, Zap, Shield, Code2, Clock,
  Users, Cpu, Server, Layers, Network, Rocket, Globe, Target, Sparkles
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const FAQItem = ({ question, answer, icon: Icon, isOpen, onClick, index }) => {
  const [itemRef, itemVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={itemRef}
      className={`transition-all duration-500 ${
        itemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <div
        className={`rounded-2xl glass-card overflow-hidden transition-all duration-300 ${
          isOpen ? 'ring-2 ring-cyan-500/30' : ''
        }`}
      >
        <button
          onClick={onClick}
          className="w-full p-5 sm:p-6 flex items-center gap-3 sm:gap-4 text-left hover:bg-white/5 transition-colors"
        >
          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isOpen ? 'bg-cyan-500/20 border border-cyan-500/30' : 'bg-white/5 border border-white/10'
          }`}>
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isOpen ? 'text-cyan-400' : 'text-muted-foreground'}`} />
          </div>
          <span className="flex-1 font-semibold text-white text-base sm:text-lg">{question}</span>
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
            isOpen ? 'bg-cyan-500/20' : 'bg-white/5'
          }`}>
            {isOpen ? (
              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </button>

        <div className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-5 sm:px-6 pb-6 pt-2">
            <div className="pl-12 sm:pl-14 text-muted-foreground leading-relaxed space-y-3 text-sm sm:text-base">
              {answer}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    // SECTION A: What is ASTRA
    {
      icon: Sparkles,
      category: "About ASTRA",
      question: "What is ASTRA and what makes it different?",
      answer: (
        <>
          <p className="mb-3">
            ASTRA is a <span className="text-cyan-400 font-medium">high-throughput pure-software HFT decision engine achieving 950M+ decisions/sec</span>.
            It represents a new category in high-frequency trading: <span className="text-white font-medium">Smart + Fast</span>.
          </p>
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/20 mb-3">
            <p className="text-white font-medium mb-2">The Market Gap:</p>
            <ul className="space-y-1 text-sm">
              <li><span className="text-red-400">FPGA solutions:</span> Fast but blind — limited to simple hardcoded logic</li>
              <li><span className="text-yellow-400">Software solutions:</span> Smart but slow — can't compete on speed</li>
              <li><span className="text-green-400">ASTRA:</span> Smart + Fast — sophisticated quant strategies at near-FPGA speeds</li>
            </ul>
          </div>
          <p className="text-sm">
            No competitor combines <span className="text-cyan-400 font-medium">950 million decisions per second throughput</span> with
            the ability to run real quantitative models like Avellaneda-Stoikov market making and Order Flow Imbalance analysis.
          </p>
        </>
      )
    },
    {
      icon: Zap,
      category: "About ASTRA",
      question: "What are ASTRA's headline performance numbers?",
      answer: (
        <>
          <p className="mb-3">Verified benchmarks on standard hardware:</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="p-4 rounded-xl bg-white/5 border border-cyan-500/20 text-center">
              <div className="text-2xl font-bold text-cyan-400">950M+/sec</div>
              <div className="text-sm text-muted-foreground">Decision Throughput (ARM64)</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-cyan-500/20 text-center">
              <div className="text-2xl font-bold text-cyan-400">150-250ns</div>
              <div className="text-sm text-muted-foreground">Target Tick-to-Trade</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-cyan-500/20 text-center">
              <div className="text-2xl font-bold text-cyan-400">~2ns</div>
              <div className="text-sm text-muted-foreground">Amortized Per-Decision</div>
            </div>
          </div>
          <p className="text-sm">
            These numbers are <span className="text-white font-medium">reproducible on your own hardware</span>.
            ASTRA's hot path uses continuous polling on a dedicated core to maintain peak throughput.
          </p>
        </>
      )
    },
    {
      icon: Code2,
      category: "Technology",
      question: "What is Codon and why does it matter?",
      answer: (
        <>
          <p className="mb-3">
            <span className="text-cyan-400 font-medium">Codon</span> is a breakthrough compiler from MIT that enables
            Python-syntax code to run at C++ speeds.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse mb-3">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Feature</th>
                  <th className="text-left py-2 text-white font-medium">Python</th>
                  <th className="text-left py-2 text-white font-medium">Codon</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-white/5"><td className="py-1.5">Syntax</td><td>Python</td><td className="text-green-400">Python</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5">Execution</td><td>Interpreted</td><td className="text-green-400">Native compiled (LLVM)</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5">Speed</td><td>~100x slower than C</td><td className="text-green-400">C++ equivalent</td></tr>
                <tr><td className="py-1.5">GIL</td><td>Yes (bottleneck)</td><td className="text-green-400">None</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm">
            ASTRA is the <span className="text-white font-medium">first known Codon-native HFT engine</span>.
            This gives us the development speed of Python with the execution speed of C++.
          </p>
        </>
      )
    },
    {
      icon: Server,
      category: "Technology",
      question: "What is the BlueField-3 DPU innovation?",
      answer: (
        <>
          <p className="mb-3">
            NVIDIA's <span className="text-cyan-400 font-medium">BlueField-3 DPU</span> (Data Processing Unit) is a
            smart network card with 16 ARM Cortex-A78 cores built directly into it.
          </p>
          <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-3">
            <p className="text-white font-medium mb-2">ASTRA's Target Architecture:</p>
            <p className="text-sm">
              ASTRA designed to run directly on the DPU's ARM cores — <span className="text-cyan-400">inside the network card itself</span>.
              This eliminates the round-trip to the server CPU, targeting <span className="text-white font-medium">150-250ns tick-to-trade latency</span>.
            </p>
          </div>
          <p className="text-sm">
            This is a fundamentally different architecture from traditional software solutions,
            combining the programmability of software with near-FPGA latency characteristics.
          </p>
        </>
      )
    },
    {
      icon: Cpu,
      category: "Technology",
      question: "How does ASTRA compare to FPGA solutions?",
      answer: (
        <>
          <p className="mb-3"><span className="text-cyan-400 font-medium">Honest comparison:</span></p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse mb-3">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Factor</th>
                  <th className="text-left py-2 text-white font-medium">FPGA</th>
                  <th className="text-left py-2 text-white font-medium">ASTRA</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-white/5"><td className="py-1.5">Tick-to-trade</td><td>100-500ns</td><td>150-250ns (target w/ DPU)</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5">Strategy complexity</td><td className="text-red-400">Very limited</td><td className="text-green-400">Full quant models</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5">Development time</td><td>6-18 months</td><td className="text-green-400">Days</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5">Strategy updates</td><td>Weeks</td><td className="text-green-400">Minutes</td></tr>
                <tr><td className="py-1.5">Quant research</td><td className="text-red-400">Cannot run</td><td className="text-green-400">Native support</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm">
            <span className="text-white font-medium">Bottom line:</span> FPGA wins on raw tick-to-trade,
            but can only run simple hardcoded logic. ASTRA runs sophisticated quant strategies at near-FPGA speeds.
          </p>
        </>
      )
    },
    {
      icon: Layers,
      category: "Technology",
      question: "What strategy components does ASTRA support?",
      answer: (
        <>
          <p className="mb-3">ASTRA includes sophisticated <span className="text-cyan-400 font-medium">quantitative strategy components</span>:</p>
          <div className="space-y-3 mb-3">
            <div className="p-3 rounded-lg bg-white/5 border border-cyan-500/20">
              <p className="text-cyan-400 font-medium">Market Making</p>
              <p className="text-sm">Avellaneda-Stoikov model with inventory risk management and optimal quote placement</p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 border border-purple-500/20">
              <p className="text-purple-400 font-medium">Order Flow Imbalance (OFI)</p>
              <p className="text-sm">Real-time analysis of buy/sell pressure from order book dynamics</p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 border border-blue-500/20">
              <p className="text-blue-400 font-medium">Regime Detection</p>
              <p className="text-sm">Statistical models that identify market regime shifts (trending, mean-reverting, volatile)</p>
            </div>
          </div>
          <p className="text-sm">
            These are <span className="text-white font-medium">production-ready components</span>, not toy examples.
            The same models used by top quantitative funds, running at nanosecond speeds.
          </p>
        </>
      )
    },
    {
      icon: Network,
      category: "Capabilities",
      question: "What protocols and exchanges does ASTRA support?",
      answer: (
        <>
          <p className="mb-3"><span className="text-cyan-400 font-medium">6 native protocol implementations</span> covering 60+ global exchanges:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse mb-3">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Protocol</th>
                  <th className="text-left py-2 text-white font-medium">Use Case</th>
                  <th className="text-left py-2 text-white font-medium">Coverage</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-white/5"><td className="py-1.5 text-cyan-400">FIX 4.0/4.2/4.4/5.0</td><td>Order entry</td><td>NYSE, LSE, TSE, most global</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5 text-cyan-400">SBE</td><td>Binary encoding</td><td>CME, EUREX, ASX</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5 text-cyan-400">ITCH</td><td>Market data</td><td>NASDAQ, Nordic</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5 text-cyan-400">OUCH</td><td>Order entry</td><td>NASDAQ</td></tr>
                <tr className="border-b border-white/5"><td className="py-1.5 text-cyan-400">FAST</td><td>Market data</td><td>MOEX, commodities</td></tr>
                <tr><td className="py-1.5 text-cyan-400">WebSocket</td><td>Crypto trading</td><td>Binance, Coinbase, Kraken</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm">
            All protocols are <span className="text-white font-medium">implemented from scratch</span> —
            zero external dependencies in the hot path.
          </p>
        </>
      )
    },
    {
      icon: Target,
      category: "Market Focus",
      question: "Why is ASTRA focused on India and NSE?",
      answer: (
        <>
          <p className="mb-3">
            The <span className="text-cyan-400 font-medium">National Stock Exchange of India (NSE)</span> is
            the world's largest derivatives exchange by volume and a high-growth market.
          </p>
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-3">
            <p className="text-white font-medium mb-2">India-First Strategy:</p>
            <ul className="space-y-1 text-sm">
              <li>• NSE co-location is the initial deployment target</li>
              <li>• SEBI compliance built into the architecture from day one</li>
              <li>• Local market expertise and relationships</li>
              <li>• Growing HFT adoption with limited local solutions</li>
            </ul>
          </div>
          <p className="text-sm">
            After establishing in India, ASTRA's protocol support enables
            <span className="text-white font-medium"> expansion to 60+ global exchanges</span>.
          </p>
        </>
      )
    },
    {
      icon: Shield,
      category: "Compliance",
      question: "How does ASTRA handle regulatory compliance?",
      answer: (
        <>
          <p className="mb-3"><span className="text-green-400 font-medium">Compliance is architectural, not an afterthought:</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="space-y-2">
              <p className="text-white font-medium text-sm">SEBI (India):</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2 text-sm">
                <li>Order-to-Trade Ratio (OTR) monitoring</li>
                <li>Hardware kill switch capability</li>
                <li>Complete audit trail</li>
                <li>5-year data retention</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="text-white font-medium text-sm">Global Standards:</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2 text-sm">
                <li>MiFID II RTS 6 & RTS 25</li>
                <li>SEC Rule 17a-4</li>
                <li>Anti-manipulation detection</li>
                <li>IEEE 1588 PTP clock sync</li>
              </ul>
            </div>
          </div>
          <p className="text-sm">
            Built by a team with <span className="text-white font-medium">14+ years implementing enterprise compliance</span>.
            All compliance features run in the critical path without adding latency.
          </p>
        </>
      )
    },
    {
      icon: Clock,
      category: "Development",
      question: "What is the current development status?",
      answer: (
        <>
          <p className="mb-3">
            ASTRA has been in development for <span className="text-cyan-400 font-medium">11+ months</span> with
            a significant codebase:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <div className="text-xl font-bold text-cyan-400">15,000+</div>
              <div className="text-xs text-muted-foreground">Lines of Code</div>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <div className="text-xl font-bold text-cyan-400">50+</div>
              <div className="text-xs text-muted-foreground">Source Files</div>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <div className="text-xl font-bold text-cyan-400">6</div>
              <div className="text-xs text-muted-foreground">Native Protocols</div>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <div className="text-xl font-bold text-cyan-400">6</div>
              <div className="text-xs text-muted-foreground">Toggleable Modules</div>
            </div>
          </div>
          <p className="text-sm">
            The core decision engine, risk management, compliance framework, and all protocol codecs are
            <span className="text-white font-medium"> complete and benchmarked</span>.
          </p>
        </>
      )
    },
    {
      icon: Users,
      category: "Team",
      question: "Who is behind ASTRA?",
      answer: (
        <>
          <p className="mb-3">ASTRA is built by a team with deep enterprise technology and security expertise:</p>
          <div className="space-y-3 mb-3">
            <div className="p-4 rounded-lg bg-white/5 border border-cyan-500/20">
              <p className="text-cyan-400 font-medium">Santosh T — Founder & CEO</p>
              <p className="text-sm mt-1">
                14+ years in enterprise technology across security architecture, network engineering,
                and technical leadership at global technology companies.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-purple-500/20">
              <p className="text-purple-400 font-medium">Suresh Sharma — Strategic Advisor & Co-Founder</p>
              <p className="text-sm mt-1">
                30+ years in cybersecurity. Former CISO at PayU and Motilal Oswal.
                Brings deep expertise in financial services security and compliance.
              </p>
            </div>
          </div>
          <p className="text-sm">
            The team's background in <span className="text-white font-medium">enterprise security and compliance</span>
            is why regulatory features are built into ASTRA's architecture from day one.
          </p>
        </>
      )
    },
    {
      icon: Globe,
      category: "Vision",
      question: "What is ASTRA's expansion roadmap?",
      answer: (
        <>
          <p className="mb-3">ASTRA is designed for <span className="text-cyan-400 font-medium">global expansion</span> from day one:</p>
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-sm">1</div>
              <div>
                <p className="text-white font-medium text-sm">NSE Co-location (India)</p>
                <p className="text-xs text-muted-foreground">Initial deployment target</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">2</div>
              <div>
                <p className="text-white font-medium text-sm">Asia-Pacific Expansion</p>
                <p className="text-xs text-muted-foreground">SGX, ASX, TSE with existing protocol support</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-sm">3</div>
              <div>
                <p className="text-white font-medium text-sm">Global Markets</p>
                <p className="text-xs text-muted-foreground">NYSE, NASDAQ, CME, LSE — all protocols ready</p>
              </div>
            </div>
          </div>
          <p className="text-sm">
            The 6 native protocol implementations already support <span className="text-white font-medium">60+ global exchanges</span>.
          </p>
        </>
      )
    },
    {
      icon: Rocket,
      category: "Vision",
      question: "What problem is ASTRA solving for the industry?",
      answer: (
        <>
          <p className="mb-3">
            The trading infrastructure market has a <span className="text-cyan-400 font-medium">fundamental gap</span>:
          </p>
          <div className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-yellow-500/10 border border-red-500/20 mb-3">
            <p className="text-white font-medium mb-2">The Problem:</p>
            <ul className="space-y-1 text-sm">
              <li><span className="text-red-400">Tier 1 (FPGA):</span> Incredibly fast but can only run simple, hardcoded logic</li>
              <li><span className="text-yellow-400">Tier 2 (Software):</span> Flexible but too slow for competitive HFT</li>
            </ul>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/20 mb-3">
            <p className="text-white font-medium mb-2">ASTRA's Solution:</p>
            <p className="text-sm">
              A <span className="text-cyan-400 font-medium">new category</span> that combines near-FPGA speed with
              the ability to run sophisticated quantitative strategies. No more choosing between fast and smart.
            </p>
          </div>
          <p className="text-sm">
            This addresses the needs of firms that want to compete on speed
            <span className="text-white font-medium"> without sacrificing strategy sophistication</span>.
          </p>
        </>
      )
    }
  ];

  return (
    <section id="faq" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <HelpCircle className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Understanding ASTRA</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Everything you need to know about ASTRA — the technology, the team, and the vision.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              icon={faq.icon}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl glass-card">
            <p className="text-muted-foreground">
              Want to learn more? Let's connect.
            </p>
            <a
              href="#contact"
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
